import { UpdateGlobalDashboardFiltersParams } from "../api/dashboard.api";
import { DashboardGlobalFiltersState } from "../contexts/DashboardContext";

/**
 * Helper to transform the global filters object into the shape required
 * by the API to persist the user's global dashboard filter preferences
 *
 * @param filters state of global filters object
 * @param dashboardId relevant dashboard we're updating
 * @returns shape required to persist update of global dashboard filters
 *
 * @example
 *
 * **In:**
 ```
 const dashboardId = 3;
 const globalFilters = {
    dateRangeOptions: {
      days: 30,
      custom: null
    },
    filterByType: {
      id: 1,
      filterBy: "customer",
      filterOptions: { id: null }
    },
    filterByValue: { id: 1 },
    customer: { ... },
    drop: null
  };
 ```
 *
 *
 * **Out:**
 * ```
 * const result = [
    {
      dashboardId: 3,
      filterTypeId: 3,
      filterValue: {
        dateRange: {
          days: 30,
          custom: null
        }
      }
    },
    {
      dashboardId: 3,
      filterTypeId: 1,
      filterValue: { customer: { id: 1 } }
    }
  ];
 * ```
 *
 */
const transformGlobalFilters = (filters: DashboardGlobalFiltersState, dashboardId: number): UpdateGlobalDashboardFiltersParams["params"] => {
  const transformedFilters: UpdateGlobalDashboardFiltersParams["params"] = [];

  // Handle the date preference
  if (filters.dateRangeFilterTypeId && filters.dateRangeOptions) {
    transformedFilters.push({
      dashboardId,
      filterTypeId: filters.dateRangeFilterTypeId,
      filterValue: { dateRange: filters.dateRangeOptions }
    });
  }

  // Handle the customer / branch preference
  if (filters.filterByType && filters.filterByValue) {
    transformedFilters.push({
      dashboardId,
      filterTypeId: filters.filterByType.id,
      filterValue: { [ filters.filterByType.filterBy ]: { id: filters.filterByValue.id } }
    });
  }

  return transformedFilters;
};

export default transformGlobalFilters;
